import { useLang, CONTACTS } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn } from "../components/Reveal";
import { PageHero } from "../components/Bits";

const privacy = {
  it: {
    title: "Privacy Policy | Nicole Srl",
    desc: "Informativa sul trattamento dei dati personali di Nicole Srl ai sensi del GDPR.",
    kicker: "Legale",
    lines: ["Privacy Policy"],
    updated: "Ultimo aggiornamento: luglio 2026",
    sections: [
      {
        h: "Titolare del trattamento",
        p: `Il titolare del trattamento dei dati personali è Nicole Srl, con sede in ${CONTACTS.address}, contattabile al telefono ${CONTACTS.phone} o per iscritto all'indirizzo della sede.`,
      },
      {
        h: "Dati raccolti e finalità",
        p: "Attraverso il modulo contatti raccogliamo: nome e cognome, telefono, email, comune dell'immobile, tipo di richiesta e messaggio. I dati sono trattati esclusivamente per rispondere alla richiesta inviata, valutare l'eventuale operazione immobiliare e ricontattare l'interessato. La base giuridica è l'esecuzione di misure precontrattuali su richiesta dell'interessato (art. 6, par. 1, lett. b GDPR) e il consenso espresso tramite apposita casella.",
      },
      {
        h: "Modalità e conservazione",
        p: "I dati sono trattati con strumenti informatici, con misure di sicurezza adeguate, e conservati per il tempo necessario a gestire la richiesta e, in caso di operazione, per la durata dei relativi adempimenti. I dati non sono ceduti a terzi né diffusi; possono essere condivisi, solo se necessario all'operazione, con professionisti incaricati (tecnici, notai, legali).",
      },
      {
        h: "Diritti dell'interessato",
        p: `In qualsiasi momento è possibile esercitare i diritti previsti dagli artt. 15-22 GDPR (accesso, rettifica, cancellazione, limitazione, portabilità, opposizione) telefonando al ${CONTACTS.phone} o scrivendo all'indirizzo della sede. È inoltre possibile proporre reclamo al Garante per la protezione dei dati personali.`,
      },
    ],
  },
  en: {
    title: "Privacy Policy | Nicole Srl",
    desc: "Information on the processing of personal data by Nicole Srl pursuant to the GDPR.",
    kicker: "Legal",
    lines: ["Privacy Policy"],
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "Data controller",
        p: `The data controller is Nicole Srl, based at ${CONTACTS.address}, reachable by phone at ${CONTACTS.phone} or in writing at the registered office address.`,
      },
      {
        h: "Data collected and purposes",
        p: "Through the contact form we collect: full name, phone, email, property municipality, request type and message. Data is processed exclusively to respond to the request, assess the potential real estate operation and contact the person back. The legal basis is the performance of pre-contractual measures at the request of the data subject (Art. 6(1)(b) GDPR) and the consent given via the dedicated checkbox.",
      },
      {
        h: "Methods and retention",
        p: "Data is processed electronically with appropriate security measures and retained for the time needed to handle the request and, in case of an operation, for the duration of the related obligations. Data is not sold or disclosed to third parties; it may be shared, only if necessary for the operation, with appointed professionals (surveyors, notaries, lawyers).",
      },
      {
        h: "Data subject rights",
        p: `At any time you may exercise the rights under Articles 15-22 GDPR (access, rectification, erasure, restriction, portability, objection) by calling ${CONTACTS.phone} or writing to the registered office address. You may also lodge a complaint with the Italian Data Protection Authority (Garante).`,
      },
    ],
  },
};

const cookie = {
  it: {
    title: "Cookie Policy | Nicole Srl",
    desc: "Informativa sui cookie del sito nicolesrlmilano.com.",
    kicker: "Legale",
    lines: ["Cookie Policy"],
    updated: "Ultimo aggiornamento: luglio 2026",
    sections: [
      {
        h: "Cookie tecnici",
        p: "Questo sito utilizza esclusivamente cookie e strumenti tecnici necessari al funzionamento delle pagine. Non vengono installati cookie di profilazione propri e non viene richiesto alcun consenso preventivo per i cookie tecnici.",
      },
      {
        h: "Servizi di terze parti",
        p: "Il sito utilizza Google Search Console per monitorare la propria presenza nei risultati di ricerca Google: questo strumento non installa cookie sui visitatori e non raccoglie dati personali di navigazione. Il sito resta predisposto per l'eventuale attivazione futura di strumenti di analisi (Google Analytics 4, Meta Pixel), al momento non attivi. Qualora venissero attivati, questa informativa sarà aggiornata e, ove richiesto, sarà mostrato un banner per la raccolta del consenso.",
      },
      {
        h: "Gestione delle preferenze",
        p: "È possibile gestire o eliminare i cookie direttamente dalle impostazioni del proprio browser, nella sezione dedicata a privacy e sicurezza.",
      },
    ],
  },
  en: {
    title: "Cookie Policy | Nicole Srl",
    desc: "Cookie information for the website nicolesrlmilano.com.",
    kicker: "Legal",
    lines: ["Cookie Policy"],
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "Technical cookies",
        p: "This website uses only technical cookies and tools necessary for the pages to function. No first-party profiling cookies are installed and no prior consent is required for technical cookies.",
      },
      {
        h: "Third-party services",
        p: "This website uses Google Search Console to monitor its presence in Google search results: this tool does not install cookies on visitors and does not collect personal browsing data. The website remains prepared for the possible future activation of analytics tools (Google Analytics 4, Meta Pixel), which are currently not active. Should they be activated, this policy will be updated and, where required, a consent banner will be displayed.",
      },
      {
        h: "Managing preferences",
        p: "You can manage or delete cookies directly from your browser settings, in the privacy and security section.",
      },
    ],
  },
};

const LegalPage = ({ data, path }) => {
  const { lang } = useLang();
  const t = data[lang];
  useSEO({ title: t.title, description: t.desc, path, lang });
  return (
    <div data-testid={`${path.slice(1)}-page`}>
      <PageHero kicker={t.kicker} lines={t.lines} />
      <section className="mx-auto max-w-3xl px-6 pb-28 md:px-10 md:pb-40">
        <FadeIn>
          <p className="text-[11px] uppercase tracking-[0.22em] text-stone">{t.updated}</p>
        </FadeIn>
        {t.sections.map((s, i) => (
          <FadeIn key={s.h} delay={i * 0.05}>
            <div className="mt-14 border-b border-ink/10 pb-14">
              <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">{s.h}</h2>
              <p className="mt-5 text-base leading-relaxed text-stone">{s.p}</p>
            </div>
          </FadeIn>
        ))}
      </section>
    </div>
  );
};

export const PrivacyPage = () => <LegalPage data={privacy} path="/privacy-policy" />;
export const CookiePage = () => <LegalPage data={cookie} path="/cookie-policy" />;
